import React from "react";
import { useDeal } from "../lib/store";
import { Card, ScopeTag, ScenarioBar } from "../components/shared";
import * as m from "../lib/model";
import { AuditBadge } from "../components/AuditBadge";

export default function Sensitivity() {
  const { M: M0, V, scope, navigate, scenario, setScenario, scenFlash } = useDeal();
  const M = V;
  const consolidated = m.isRollupScope(V);
  const viewTag = m.scopeLabel(M0, scope);

  const baseExit = Number(M.exitMult) || 4;
  const exitRows = [-1.5, -1, -0.5, 0, 0.5, 1].map((d) => Math.round((baseExit + d) * 100) / 100).filter((x) => x >= 1);
  const askMin = 300000;
  const priceCols = Array.from({ length: 5 }, (_, i) => Math.round(askMin + ((M.askPrice - askMin) / 4) * i));

  const growthRows = [-5, 0, 2.5, 5, 10];
  const multCols = [2, 3, 4, 5, 7];

  const sdeRows = [0.7, 0.85, 1.0, 1.15, 1.3].map((f) => Math.round(M.sde * f));
  const gmCols = [30, 35, 40, 45, 50];

  const scenSummary = ["downside", "base", "upside"].map((s) => {
    const SM = m.scenarioModel(M, s);
    const ex = m.exitReturns(SM);
    const rows = m.model5yr(SM);
    return {
      s, price: SM.askPrice, salary: SM.salary, growth: SM.sdeGrowth,
      dscr: m.dscrYr1(SM), moic: ex.moic, irr: ex.irr, fcf5: rows.reduce((a, r) => a + r.fcf, 0),
    };
  });

  return (
    <div className={`page ${scenFlash ? "scen-flash" : ""}`} data-testid="page-sensitivity">
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div><div className="page-title">Sensitivity{viewTag && <span className="view-tag" data-testid="sens-view-tag">{viewTag}</span>}</div><div className="page-sub">{consolidated ? "Stress the consolidated roll-up — price and exit flex the platform; add-ons follow the Roll-Up terms" : "Stress the deal across the variables that matter most"}</div></div>
        <div className="head-ctls"><AuditBadge V={M} /><ScenarioBar value={scenario} onChange={setScenario} /><ScopeTag M={M0} value={scope} onEdit={() => navigate("assumptions")} testid="sens-scope" /></div>
      </div>

      <div className="grid g2">
        <Card title="1 · Equity IRR — Exit Multiple × Price" className="table-scroll" testid="sens-1">
          <table className="sens">
            <thead><tr><th>Exit \ Price</th>{priceCols.map((p) => <th key={p}>{m.fmt$(p)}</th>)}</tr></thead>
            <tbody>
              {exitRows.map((x) => (
                <tr key={x} className={x === baseExit ? "base-row" : ""}><td className="rowh">{x}×{x === baseExit ? " ·" : ""}</td>
                  {priceCols.map((p) => { const irr = m.exitReturns({ ...M, exitMult: x, askPrice: p }).irr; return <td key={p} className={m.sensIRR(irr)} data-testid={`sens1-${x}-${p}`}>{isNaN(irr) ? "n/a" : m.fmtPct(irr)}</td>; })}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card title="2 · MOIC — Growth × Exit Multiple" className="table-scroll" testid="sens-2">
          <table className="sens">
            <thead><tr><th>Growth \ Exit</th>{multCols.map((c) => <th key={c}>{c}×</th>)}</tr></thead>
            <tbody>
              {growthRows.map((g) => (
                <tr key={g}><td className="rowh">{g}%</td>
                  {multCols.map((c) => { const moic = m.exitReturns({ ...M, sdeGrowth: g, exitMult: c }).moic; return <td key={c} className={m.sensMOIC(moic)}>{moic.toFixed(2)}×</td>; })}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card title="3 · FCF Yr 1 — SDE × Gross Margin" className="table-scroll" testid="sens-3">
          <table className="sens">
            <thead><tr><th>SDE \ GM%</th>{gmCols.map((c) => <th key={c}>{c}%</th>)}</tr></thead>
            <tbody>
              {sdeRows.map((sv) => (
                <tr key={sv}><td className="rowh">{m.fmt$(sv)}</td>
                  {gmCols.map((c) => { const sdeAdj = sv + ((c - M.grossMarginPct) / 100) * M.ttmRevenue; const f = m.fcfYr1({ ...M, sde: sdeAdj }); return <td key={c} className={m.sensFCF(f)}>{m.fmt$(f)}</td>; })}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      <div className="grid g2" style={{ marginTop: 12 }}>
        <Card title="4 · LP IRR — Mgmt Fee × GP Promote" className="table-scroll" testid="sens-4">
          <table className="sens">
            <thead><tr><th>Fee% \ GP</th>{[10, 15, 20, 25, 30].map((p) => <th key={p}>{p}%</th>)}</tr></thead>
            <tbody>
              {[0, 1, 2, 3, 5].map((fee) => (
                <tr key={fee}><td className="rowh">{fee}%</td>
                  {[10, 15, 20, 25, 30].map((p) => { const irr = m.lpWaterfall({ ...M, mgmtFeePct: fee, lpSplit: 100 - p }).lpIrr; return <td key={p} className={m.sensIRR(irr)}>{isNaN(irr) ? "n/a" : m.fmtPct(irr)}</td>; })}
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 8 }}>Rows = annual management fee; columns = GP promote (tier 1). Cells show modeled LP IRR.</div>
        </Card>
      </div>

      <Card title="Scenario Comparison" style={{ marginTop: 12 }} className="table-scroll" testid="scen-compare">
        <table className="tbl">
          <thead><tr><th>Metric</th><th className="num">Downside</th><th className="num">Base</th><th className="num">Upside</th></tr></thead>
          <tbody>
            <tr><td>Price</td>{scenSummary.map((x) => <td className="num" key={x.s}>{m.fmt$(x.price)}</td>)}</tr>
            <tr><td>Buyer salary</td>{scenSummary.map((x) => <td className="num" key={x.s}>{m.fmt$(x.salary)}</td>)}</tr>
            <tr><td>SDE growth</td>{scenSummary.map((x) => <td className="num" key={x.s}>{x.growth}%</td>)}</tr>
            <tr><td>DSCR Yr 1</td>{scenSummary.map((x) => <td className={`num ${m.dscrTone(x.dscr) === "go" ? "pos" : m.dscrTone(x.dscr) === "pass" ? "neg" : "neu"}`} key={x.s}>{m.fmtX(x.dscr)}</td>)}</tr>
            <tr><td>Equity MOIC</td>{scenSummary.map((x) => <td className="num" key={x.s}>{m.fmtX(x.moic)}</td>)}</tr>
            <tr><td>IRR</td>{scenSummary.map((x) => <td className="num" key={x.s}>{isNaN(x.irr) ? "n/a" : m.fmtPct(x.irr)}</td>)}</tr>
            <tr><td>5-yr cumulative FCF</td>{scenSummary.map((x) => <td className={`num ${x.fcf5 >= 0 ? "pos" : "neg"}`} key={x.s}>{m.fmt$(x.fcf5)}</td>)}</tr>
          </tbody>
        </table>
      </Card>
    </div>
  );
}
